/**
 * חיווי ניצול נפח למנהל (D14 א׳ 2ג) — מסד וקבצים מול מגבלות המסלול החינמי.
 *
 * הנתונים מ־`storage_usage()`, שמחזירה סכומים למנהל בלבד. לבקר השרת מסרב,
 * והמסך אינו מציג דבר. הסף (70%) והמגבלות — `@hakshuri/domain` (usage.ts).
 *
 * 6ו-ב חלק 6 (D31): כל מד — שורה בקבוצה (`.list-item`), הפס במילוי לפי המשמעות מה־tokens;
 * role=meter וה־testids — כמו קודם.
 */

import { useEffect, useState, type ReactElement } from 'react';
import {
  formatBytes,
  FREE_PLAN_LIMITS,
  usageMeter,
  type UsageLevel,
  type UsageMeter,
} from '@hakshuri/domain';
import { describeError } from './format.ts';
import { supabase } from './supabase.ts';

export interface Usage {
  database: UsageMeter;
  files: UsageMeter;
  fileCount: number;
}

type UsageState =
  | { kind: 'loading' }
  | { kind: 'ready'; usage: Usage }
  | { kind: 'denied' }
  | { kind: 'error'; message: string };

export function useStorageUsage(refreshKey = 0): UsageState {
  const [state, setState] = useState<UsageState>({ kind: 'loading' });
  useEffect(() => {
    if (supabase === null) {
      setState({ kind: 'denied' });
      return;
    }
    let cancelled = false;
    void supabase.rpc('storage_usage').then(({ data, error }) => {
      if (cancelled) return;
      if (error !== null) {
        // 42501 — לא מנהל (או לא מחובר): אין מה להציג, וזה אינו כשל.
        setState(
          error.code === '42501'
            ? { kind: 'denied' }
            : { kind: 'error', message: describeError(error) },
        );
        return;
      }
      const row = (
        data as { database_bytes: number; files_bytes: number; file_count: number }[]
      )[0];
      setState({
        kind: 'ready',
        usage: {
          database: usageMeter(Number(row?.database_bytes), FREE_PLAN_LIMITS.databaseBytes),
          files: usageMeter(Number(row?.files_bytes), FREE_PLAN_LIMITS.filesBytes),
          fileCount: Number(row?.file_count ?? 0),
        },
      });
    });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);
  return state;
}

function worst(usage: Usage): UsageLevel {
  const levels = [usage.database.level, usage.files.level];
  return levels.includes('critical') ? 'critical' : levels.includes('warn') ? 'warn' : 'ok';
}

const WHAT_TO_DO =
  'כשמתקרבים למגבלה — לפי הסדר שנקבע (D14), וכל צעד רק באישורך: העברת קבצים ל־Cloudflare R2 (חינמי), Supabase על שרת משלנו, ורק בסוף — מסלול בתשלום.';

function Meter({ label, meter, testId }: { label: string; meter: UsageMeter; testId: string }) {
  return (
    <div className="usage-row list-item" data-testid={testId} data-level={meter.level}>
      <div className="usage-label">
        <span>{label}</span>
        {/* bdi (6ו-ב חלק 6): בלעדיו ״500MB · 24%״ היה רצף LTR אחד, ונקרא ״120MB מתוך 24% · 500MB״ */}
        <span>
          <bdi>{formatBytes(meter.usedBytes)}</bdi> מתוך <bdi>{formatBytes(meter.limitBytes)}</bdi>{' '}
          · <strong>{meter.percent}%</strong>
        </span>
      </div>
      <div
        className={`usage-bar usage-${meter.level}`}
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.min(meter.percent, 100)}
      >
        <span style={{ inlineSize: `${Math.min(meter.percent, 100)}%` }} />
      </div>
    </div>
  );
}

/** כרטיס מלא — במסך ״חשבון והרשאות״ של המנהל. */
export function UsagePanel({ refreshKey }: { refreshKey: number }): ReactElement | null {
  const state = useStorageUsage(refreshKey);
  if (state.kind === 'denied') return null;
  return (
    <section className="card" data-testid="usage-panel">
      <h2>ניצול נפח — מסלול חינמי</h2>
      {state.kind === 'loading' && <p className="muted small">בודק…</p>}
      {state.kind === 'error' && (
        <p className="note note-error">
          החיווי לא נטען: {state.message}. אם השגיאה מזכירה ש־storage_usage לא נמצאה — עדכון השרת
          לשלב 5 טרם הועלה.
        </p>
      )}
      {state.kind === 'ready' && (
        <>
          <Meter label="מסד נתונים" meter={state.usage.database} testId="usage-database" />
          <Meter
            label={`קבצים (${state.usage.fileCount.toLocaleString('he-IL')})`}
            meter={state.usage.files}
            testId="usage-files"
          />
          {worst(state.usage) !== 'ok' ? (
            <p className="note note-warn" data-testid="usage-warning">
              <strong>ניצול של 70% ומעלה.</strong> {WHAT_TO_DO}
            </p>
          ) : (
            <p className="small muted">
              אזהרה תוצג כאן, ובראש כל מסך למנהל, מ־70%. התמונות מכווצות לכ־300KB לפני העלאה.
            </p>
          )}
        </>
      )}
    </section>
  );
}

/** פס אזהרה בראש האפליקציה — רק למנהל, ורק מ־70%. */
export function UsageWarningBanner({ onOpen }: { onOpen: () => void }): ReactElement | null {
  const state = useStorageUsage();
  if (state.kind !== 'ready' || worst(state.usage) === 'ok') return null;
  const { database, files } = state.usage;
  return (
    <div className="demo-banner" role="status" data-testid="usage-banner">
      <strong>הנפח מתקרב למגבלה:</strong> מסד {database.percent}% · קבצים {files.percent}%.{' '}
      <button type="button" className="link-button" onClick={onOpen}>
        לפרטים
      </button>
    </div>
  );
}
